import ErrorService from '@core/services/ErrorService';
import BaseModel from '@core/model';

import LOASourceEnum from '@constants/enums/loa/source';
import LOAStatusEnum from '@constants/enums/loa/status';
import LOAType from '@constants/enums/loa/type';

import { toDate } from '@utils/date';

import { Provider, Investor, Agreement, MediaInvestor } from '@/models';

export class ProviderInvestor extends BaseModel {
    static name = 'ProviderInvestor';
    static entity = 'provider-investors';

    static default_investor_id = 'INVESTOR-ID';
    static default_provider_id = 'PROVIDER-ID';

    static getIds(id) {
        let result = {
            provider_id: null,
            investor_id: null
        };

        if (!id || !id.includes('_')) {
            return result;
        }

        const [investor_id, provider_id] = id.split('_');

        if (investor_id || investor_id !== this.default_investor_id) {
            result.investor_id = investor_id;
        }

        if (provider_id || provider_id !== this.default_provider_id) {
            result.provider_id = provider_id;
        }

        return result;
    }

    static fields() {
        return {
            ...super.fields(),
            id: this.string(null).nullable(),
            $sending_loa_for_signature: this.boolean(false),
            $downloading_loa_form: this.boolean(false),
            $downloading_loa_document: this.boolean(false),
            $downloading_loa_audit: this.boolean(false),

            provider_id: this.string(null).nullable(),
            provider: this.belongsTo(Provider, 'provider_id'),

            investor_id: this.string(null).nullable(),
            investor: this.belongsTo(Investor, 'investor_id'),

            loa_signing_request_id: this.string(null).nullable(),
            loa_signing_request: this.belongsTo(Agreement, 'loa_signing_request_id'),

            loa_document_id: this.string(null).nullable(),
            loa_document: this.belongsTo(MediaInvestor, 'loa_document_id'),

            loa_status: this.enum(LOAStatusEnum, LOAStatusEnum.MISSING).nullable(),
            loa_source: this.enum(LOASourceEnum).nullable(),
            loa_type: this.enum(LOAType).nullable(),
            loa_requested_at: this.string(null).nullable(),
            loa_completed_at: this.string(null).nullable()
        };
    }

    get provider_name() {
        return this.provider?.full_name || this.provider?.name || '';
    }

    get investor_name() {
        return this.investor?.full_name || this.investor?.name || '';
    }

    get file_id() {
        return this.loa_document?.file_id || null;
    }

    get audit_file_id() {
        return this.loa_document?.audit_file_id || null;
    }

    get loa_status_tooltip() {
        if (!this.loa_status) {
            return null;
        }

        if (this.loa_status === LOAStatusEnum.COMPLETED && this.loa_completed_at) {
            return `LOA completed at ${toDate(this.loa_completed_at)}`;
        }

        if (this.loa_status === LOAStatusEnum.PENDING && this.loa_requested_at) {
            return `LOA request sent at ${toDate(this.loa_requested_at)}`;
        }

        return '';
    }

    get is_requesting_loa() {
        return this.$sending_loa_for_signature === true;
    }

    get is_loa_completed() {
        return this.loa_status === LOAStatusEnum.COMPLETED;
    }

    get is_loa_pending() {
        return this.loa_status === LOAStatusEnum.PENDING;
    }

    get is_loa_missing() {
        return this.loa_status === LOAStatusEnum.MISSING;
    }

    async $getLOAMedia() {
        if (!this.loa_document_id) {
            throw new Error('No LOA document ID provided');
        }
        return await MediaInvestor.$get(this.loa_document_id);
    }

    async $update(data = null) {
        this.$updating = true;

        const investor_id = this.investor_id || this.investor?.id;
        const provider_id = this.provider_id || this.provider?.id;

        const method = data ? 'PATCH' : 'PUT';
        data = data || this.$toPayload();

        this.$saving = Object.keys(data);

        let response = null;

        try {
            if (method === 'PUT') {
                response = await Investor.api.putProviderRelationship(investor_id, provider_id, data);
            } else if (method === 'PATCH') {
                response = await Investor.api.patchProviderRelationship(investor_id, provider_id, data);
            }

            await ProviderInvestor.insertOrUpdate({ data: { ...response.data, $responseFormat: 20 } });
        } catch (err) {
            if (err?.response?.data) {
                throw err.response.data;
            } else {
                throw err;
            }
        } finally {
            this.$updating = false;
            this.$saving = [];
        }

        return ProviderInvestor.findBy({ investor_id, provider_id });
    }

    async $create() {
        this.$creating = true;

        const investor_id = this.investor_id || this.investor?.id;
        const provider_id = this.provider_id || this.provider?.id;

        let response = null;

        try {
            response = await Investor.api.postProviderRelationship(this.$toPayload());

            await ProviderInvestor.insertOrUpdate({ data: { ...response.data, $responseFormat: 20 } });
        } catch (err) {
            if (err?.response?.data) {
                throw err.response.data;
            } else {
                throw err;
            }
        } finally {
            this.$creating = false;
        }

        return ProviderInvestor.findBy({ investor_id, provider_id });
    }

    async $sendLOARequest({ investor_id = null, provider_id = null }) {
        investor_id = investor_id || (!provider_id && (this.investor_id || this.investor.id));
        provider_id = provider_id || (!investor_id && (this.provider_id || this.provider.id));

        return await this.constructor.$sendLOARequest([this], { investor_id, provider_id });
    }

    static async $sendLOARequest(
        items = [],
        {
            investor_id = null,
            provider_id = null,
            before = () => {},
            after = () => {},
            success = () => {},
            error = () => {}
        }
    ) {
        if (!Array.isArray(items) || !items.length) {
            throw new ReferenceError(`No items specified when attempting to send LOA requests`);
        }

        if (!investor_id && !provider_id) {
            throw new Error(`investor_id or provider_id must be specified when sending LOA requests`);
        } else if (investor_id && provider_id) {
            throw new Error(`Only one of investor_id or provider_id can be specified when sending LOA requests`);
        }

        const provider_ids = items.map(i => i.provider_id || i.provider?.id).filter(Boolean);
        const investor_ids = items.map(i => i.investor_id || i.investor?.id).filter(Boolean);

        for (const item of items) {
            item.$sending_loa_for_signature = true;
        }
        before(items);

        let response = null;

        try {
            if (investor_id) {
                response = await Investor.api.requestProviderLOA(investor_id, provider_ids);

                items = response.data;
                await ProviderInvestor.insertOrUpdate({ data: items });
            } else {
                const requests = async id => await Investor.api.requestProviderLOA(investor_ids[0], [id]);
                response = await Promise.all(provider_ids.map(requests));

                items = response.map(r => r.data[0]);
                await ProviderInvestor.insertOrUpdate({ data: items });
            }
            success(items);
        } catch (err) {
            ErrorService.onError(err, () =>
                this.$sendLOARequest(items, { investor_id, provider_id, before, after, success, error })
            );
            error(err);
        } finally {
            for (const item of items) {
                item.$sending_loa_for_signature = false;
            }
            after(items);
        }

        return response;
    }

    async $downloadLOAForm({ investor_id = null, provider_id = null }) {
        investor_id = investor_id || (!provider_id && (this.investor_id || this.investor.id));
        provider_id = provider_id || (!investor_id && (this.provider_id || this.provider.id));

        return await this.constructor.$downloadLOAForm([this], { investor_id, provider_id });
    }

    static async $downloadLOAForm(
        items = [],
        {
            investor_id = null,
            provider_id = null,
            before = () => {},
            after = () => {},
            success = () => {},
            error = () => {}
        }
    ) {
        if (!Array.isArray(items) || !items.length) {
            throw new ReferenceError(`No items specified when attempting to download LOA form`);
        }

        if (!investor_id && !provider_id) {
            throw new Error(`investor_id or provider_id must be specified when downloading LOA forms`);
        } else if (investor_id && provider_id) {
            throw new Error(`Only one of investor_id or provider_id can be specified when downloading LOA forms`);
        }

        const provider_ids = items.map(i => i.provider_id || i.provider?.id).filter(Boolean);
        const investor_ids = items.map(i => i.investor_id || i.investor?.id).filter(Boolean);

        for (const item of items) {
            item.$downloading_loa_form = true;
        }
        before(items);

        let response = null;

        try {
            if (investor_id) {
                response = await Investor.api.downloadProviderLOA(investor_id, provider_ids, true);
            } else {
                const requests = async id => await Investor.api.downloadProviderLOA(investor_ids[0], [id], true);
                response = await Promise.all(provider_ids.map(requests));
            }

            success(response);
        } catch (err) {
            ErrorService.onError(err, () =>
                this.$downloadLOAForm(items, { investor_id, provider_id, before, after, success, error })
            );
            error(err);
        } finally {
            for (const item of items) {
                item.$downloading_loa_form = false;
            }
            after(items);
        }

        return response;
    }

    async $downloadLOADocument() {
        return await this.constructor.$downloadLOADocument([this]);
    }

    static async $downloadLOADocument(
        items = [],
        { before = () => {}, after = () => {}, success = () => {}, error = () => {} }
    ) {
        if (!Array.isArray(items) || !items.length) {
            throw new ReferenceError(`No items specified when attempting to download LOA documents`);
        }

        if (!items.every(item => item.loa_document_id)) {
            throw new Error(`All relationships must have a loa_document_id when attempting to download LOA documents`);
        }

        for (const item of items) {
            item.$downloading_loa_document = true;
        }
        before(items);

        let response = null;

        try {
            await Promise.all(items.map(item => MediaInvestor.api.download(item.loa_document_id)));

            success(response);
        } catch (err) {
            ErrorService.onError(err, () => this.$downloadLOADocument(items, { before, after, success, error }));
            error(err);
        } finally {
            for (const item of items) {
                item.$downloading_loa_document = false;
            }
            after(items);
        }

        return response;
    }

    async $downloadLOAAudit() {
        return await this.constructor.$downloadLOAAudit([this]);
    }

    static async $downloadLOAAudit(
        items = [],
        { before = () => {}, after = () => {}, success = () => {}, error = () => {} }
    ) {
        if (!Array.isArray(items) || !items.length) {
            throw new ReferenceError(`No items specified when attempting to download LOA audits`);
        }

        if (!items.every(item => item.loa_document_id)) {
            throw new Error(`All relationships must have a loa_document_id when attempting to download LOA audits`);
        }

        for (const item of items) {
            item.$downloading_loa_audit = true;
        }
        before(items);

        let response = null;

        try {
            await Promise.all(items.map(item => MediaInvestor.api.download(item.loa_document_id, 'audit')));

            success(response);
        } catch (err) {
            ErrorService.onError(err, () => this.$downloadLOAAudit(items, { before, after, success, error }));
            error(err);
        } finally {
            for (const item of items) {
                item.$downloading_loa_audit = false;
            }
            after(items);
        }

        return response;
    }
}

export default ProviderInvestor;
